import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Select, SelectItem, Checkbox } from '@nextui-org/react';
import { DateRange } from 'rsuite/DateRangePicker';

import MultiSelectWithActions from '../../common/MultiSelectWithActions';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import {
  getSettlementsEvolutionDataOptimized,
  getAvailableEvolutionFilters,
  type EvolutionFilters,
} from 'common/src/api/settlements';
import {
  SettlementSalesData,
  SettlementMethodology,
  SettlementGroupBy,
  SettlementDateField,
} from '../../../types/settlements';
import { Session } from '@supabase/supabase-js';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { SalesChartTable, SalesExcelButton } from './SalesChartTable';
import { EmptyState } from '../../ui';

interface ColorEvolutionChartProps {
  methodology: SettlementMethodology;
  groupBy: SettlementGroupBy;
  dateField: SettlementDateField;
  dateRange: DateRange | null;
  selectedMarkets: string[];
  session: Session | null;
  userProfile: any;
  selectedSpecies: any;
  selectedSeason: string;
  visualization?: 'chart' | 'table';
}

const ColorEvolutionChart: React.FC<ColorEvolutionChartProps> = ({
  methodology,
  groupBy,
  dateField,
  dateRange,
  selectedMarkets,
  session,
  userProfile,
  selectedSpecies,
  selectedSeason,
  visualization = 'chart',
}) => {
  const { t } = useTranslation('settlementsComparative');
  const { darkMode } = useUserStore();
  // Data and loading states
  const [data, setData] = useState<SettlementSalesData | null>(null);
  const [currency, setCurrency] = useState<string>('USD');
  const [totalKilos, setTotalKilos] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Available filters
  const [availableFilters, setAvailableFilters] = useState<EvolutionFilters>({
    varieties: [],
    calibers: [],
    formats: [],
    colors: [],
  });
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Chart states
  const [selectedColorsToShow, setSelectedColorsToShow] = useState<string[]>(
    []
  );
  const [selectedVarieties, setSelectedVarieties] = useState<string[]>([]);
  const [selectedCaliber, setSelectedCaliber] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [showOwnPrices, setShowOwnPrices] = useState(false);

  const ownExporterId = userProfile?.exporter_id?.toString();

  // Check if user is demo
  const isDemo = !!userProfile?.exporter?.hasDemo;

  // Fetch available filters
  const fetchAvailableFilters = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    if (selectedSpecies.id !== 2) {
      return;
    }

    setFiltersLoading(true);

    const startDate = dateRange
      ? dateRange[0]?.toISOString().split('T')[0]
      : undefined;
    const endDate = dateRange
      ? dateRange[1]?.toISOString().split('T')[0]
      : undefined;

    try {
      const filtersData = await getAvailableEvolutionFilters(
        session,
        selectedSeason,
        selectedSpecies.id,
        startDate,
        endDate,
        dateField,
        selectedMarkets.length > 0 ? selectedMarkets : undefined
      );

      setAvailableFilters(filtersData);

      console.log('Color evolution - Available filters loaded:', {
        varieties: filtersData.varieties.length,
        calibers: filtersData.calibers.length,
        formats: filtersData.formats.length,
        colors: filtersData.colors.length,
      });
    } catch (err) {
      console.error('Error fetching available filters:', err);
    } finally {
      setFiltersLoading(false);
    }
  };

  // Fetch evolution data
  const fetchEvolutionData = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    if (selectedSpecies.id !== 2) {
      return;
    }

    setLoading(true);

    const startDate = dateRange
      ? dateRange[0]?.toISOString().split('T')[0]
      : undefined;
    const endDate = dateRange
      ? dateRange[1]?.toISOString().split('T')[0]
      : undefined;

    try {
      console.log('ColorChart API call with parameters:', {
        evolution_group_by: 'color',
        filter_variety:
          selectedVarieties.length > 0 ? selectedVarieties[0] : undefined,
        filter_caliber: selectedCaliber,
        filter_formats: selectedFormats,
        filter_colors: undefined, // no color filter for color chart
      });

      const evolutionData = await getSettlementsEvolutionDataOptimized(
        session,
        userProfile.exporter_id,
        methodology,
        selectedSeason,
        'color', // evolution grouping by color
        dateField,
        selectedSpecies.id,
        startDate,
        endDate,
        groupBy,
        selectedMarkets.length > 0 ? selectedMarkets : undefined,
        'none', // separation
        selectedVarieties.length > 0 ? selectedVarieties : undefined,
        selectedCaliber,
        selectedFormats,
        undefined // no color filter for color chart
      );

      if (
        evolutionData &&
        typeof evolutionData === 'object' &&
        'data' in evolutionData
      ) {
        // New API response format with currency and total kilos
        setData(evolutionData.data);
        setCurrency(evolutionData.currency || 'USD');
        setTotalKilos(evolutionData.totalKilos || null);
      } else {
        // Fallback for old format
        setData(evolutionData);
        setCurrency('USD');
        setTotalKilos(null);
      }
      console.log('Color evolution data received:', {
        dataKeys: Object.keys(evolutionData || {}),
        sampleData: evolutionData ? Object.keys(evolutionData)[0] : null,
        allKeys: evolutionData ? Object.keys(evolutionData) : [],
      });
    } catch (err) {
      console.error('Error fetching evolution data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filters on mount and when dependencies change
  useEffect(() => {
    fetchAvailableFilters();
  }, [selectedMarkets, dateRange, dateField, selectedSeason, selectedSpecies]);

  // Auto-select first options when available filters change
  useEffect(() => {
    if (
      availableFilters.varieties.length > 0 &&
      selectedVarieties.length === 0
    ) {
      if (isDemo) {
        // For demo users, only select LAPINS if available
        const lapinsVariety = availableFilters.varieties.find(
          (v) => v.value.toUpperCase() === 'LAPINS'
        );
        if (lapinsVariety) {
          console.log('Color chart - Auto-selecting LAPINS for demo user');
          setSelectedVarieties([lapinsVariety.value]);
        }
      } else {
        console.log(
          'Color chart - Auto-selecting varieties from useEffect:',
          availableFilters.varieties.map((v) => v.value)
        );
        setSelectedVarieties(availableFilters.varieties.map((v) => v.value));
      }
    }
    if (availableFilters.calibers.length > 0 && !selectedCaliber) {
      console.log(
        'Color chart - Auto-selecting caliber from useEffect:',
        availableFilters.calibers[0].value
      );
      setSelectedCaliber(availableFilters.calibers[0].value);
    }
    if (availableFilters.formats.length > 0 && selectedFormats.length === 0) {
      console.log(
        'Color chart - Auto-selecting formats from useEffect:',
        availableFilters.formats.map((f) => f.value)
      );
      setSelectedFormats(availableFilters.formats.map((f) => f.value));
    }
  }, [availableFilters, isDemo]);

  // Auto-select colors when data loads
  useEffect(() => {
    if (data && selectedColorsToShow.length === 0) {
      const availableColors = Object.keys(data);
      const firstColors = availableColors.slice(0, 3);
      console.log('Color chart - Auto-selecting colors:', firstColors);
      setSelectedColorsToShow(firstColors);
    }
  }, [data]);

  // Fetch data when filters change (excluding selectedColorsToShow for visual filtering only)
  useEffect(() => {
    if (
      selectedVarieties.length > 0 &&
      selectedCaliber &&
      selectedFormats.length > 0
    ) {
      console.log('Color chart - Fetching evolution data with filters:', {
        selectedVarieties: selectedVarieties.length,
        selectedCaliber,
        selectedFormats: selectedFormats.length,
      });
      fetchEvolutionData();
    }
  }, [
    methodology,
    groupBy,
    selectedMarkets,
    dateRange,
    dateField,
    selectedVarieties,
    selectedCaliber,
    selectedFormats,
    // selectedColorsToShow, // Removed - we want all colors loaded, then filter visually
  ]);

  const processedData = useMemo((): SettlementSalesData | null => {
    if (!data) {
      return null;
    }

    return data as SettlementSalesData;
  }, [data, methodology, ownExporterId]);

  const prepareColorEvolutionChartData = (
    chartData: SettlementSalesData | null,
    colorsFilter: string[]
  ) => {
    console.log('Color chart - Preparing chart data:', {
      hasChartData: !!chartData,
      chartDataKeys: chartData ? Object.keys(chartData) : [],
      colorsFilterLength: colorsFilter.length,
      colorsFilter: colorsFilter.slice(0, 3), // show first 3 for debugging
    });

    if (!chartData) {
      return { categories: [], series: [] };
    }

    const colorsToShow =
      colorsFilter.length > 0 ? colorsFilter : Object.keys(chartData);

    console.log('Colors to show:', colorsToShow);

    // Get all date keys from all colors
    const allDateKeys = new Set<string>();
    colorsToShow.forEach((color) => {
      if (chartData[color]) {
        Object.keys(chartData[color]).forEach((dateKey) =>
          allDateKeys.add(dateKey)
        );
      } else {
        console.log(`Color ${color} not found in chartData`);
      }
    });

    const dateKeys = Array.from(allDateKeys).sort();
    console.log(
      'Date keys found:',
      dateKeys.length,
      'first few:',
      dateKeys.slice(0, 3)
    );

    // Create series for each color
    const series: any[] = [];

    colorsToShow.forEach((color, index) => {
      const baseColor = `hsl(${index * (360 / colorsToShow.length)}, 70%, 50%)`;
      const lightColor = `hsl(${
        index * (360 / colorsToShow.length)
      }, 70%, 80%)`;

      const colorData = chartData[color];
      if (!colorData) return;

      // Own data series - only include if showOwnPrices is true
      if (showOwnPrices) {
        const ownData: Array<{ x: string; y: number | null }> = [];

        dateKeys.forEach((dateKey) => {
          const dateData = colorData[dateKey];
          let ownPrice = null;

          if (dateData) {
            // Aggregate all color entries for this date
            let ownTotalKilos = 0;
            let ownTotalSales = 0;

            Object.values(dateData).forEach((colorEntryData: any) => {
              // Calculate own aggregated price
              if (
                colorEntryData.own &&
                Array.isArray(colorEntryData.own) &&
                colorEntryData.own.length > 0 &&
                colorEntryData.ownKg &&
                colorEntryData.ownKg > 0
              ) {
                const ownValue = colorEntryData.own[0]?.value;
                if (ownValue && typeof ownValue === 'number') {
                  ownTotalKilos += colorEntryData.ownKg;
                  ownTotalSales += ownValue * colorEntryData.ownKg;
                }
              }
            });

            // Calculate weighted average prices
            if (ownTotalKilos > 0) {
              ownPrice = ownTotalSales / ownTotalKilos;
            }
          }

          ownData.push({ x: dateKey, y: ownPrice });
        });

        // Add own series (solid line, lighter color)
        series.push({
          name: `${color} - ${t('Tu Exportadora')}`,
          data: ownData,
          color: lightColor,
          type: 'line',
          stroke: {
            width: 3,
            dashArray: 0, // Solid line
          },
        });
      }

      // Others data series - always included
      const othersData: Array<{ x: string; y: number | null }> = [];

      dateKeys.forEach((dateKey) => {
        const dateData = colorData[dateKey];
        let othersPrice = null;

        if (dateData) {
          // Aggregate all color entries for this date
          let othersTotalKilos = 0;
          let othersTotalSales = 0;

          Object.values(dateData).forEach((colorEntryData: any) => {
            // Calculate others aggregated price (using median)
            if (
              colorEntryData.others &&
              colorEntryData.others.median &&
              colorEntryData.others.median > 0 &&
              colorEntryData.othersKg &&
              colorEntryData.othersKg > 0
            ) {
              othersTotalKilos += colorEntryData.othersKg;
              othersTotalSales +=
                colorEntryData.others.median * colorEntryData.othersKg;
            }
          });

          // Calculate weighted average prices
          if (othersTotalKilos > 0) {
            othersPrice = othersTotalSales / othersTotalKilos;
          }
        }

        othersData.push({ x: dateKey, y: othersPrice });
      });

      // Add others series (dashed line, base color)
      series.push({
        name: `${color} - ${
          methodology === 'by_volume' ? t('Mercado') : t('Otras Exportadoras')
        }`,
        data: othersData,
        color: baseColor,
        type: 'line',
        stroke: {
          width: 2,
          dashArray: 0, // Solid line
        },
      });
    });

    return {
      categories: dateKeys,
      series: series,
    };
  };

  const chartData = useMemo(
    () => prepareColorEvolutionChartData(processedData, selectedColorsToShow),
    [processedData, selectedColorsToShow, showOwnPrices]
  );

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        height: 400,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        zoom: {
          enabled: true,
        },
        toolbar: {
          show: true,
        },
      },
      stroke: {
        curve: 'smooth',
        width: chartData.series.map((s) => s.stroke?.width || 2),
        dashArray: chartData.series.map((s) => s.stroke?.dashArray || 0),
      },
      xaxis: {
        categories: chartData.categories,
        title: {
          text: groupBy === 'week' ? t('Semana') : t('Día'),
        },
        labels: {
          formatter: (value) => {
            if (typeof value !== 'string') {
              return value;
            }
            if (groupBy === 'week') {
              const parts = value.split('-W');
              return parts.length > 1 ? parts[1] : value;
            } else {
              const parts = value.split('-');
              if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}`;
              }
              return value;
            }
          },
          rotate: -45,
        },
      },
      yaxis: {
        title: {
          text: `${t('Precio')} (${currency})`,
        },
        labels: {
          formatter: (value) => {
            if (!value) return '';
            const symbol = currency === 'RMB' ? '¥' : 'USD';
            return `${value.toFixed(2)} ${symbol}`;
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value) =>
            value ? `$${value.toFixed(2)}/kg` : t('Sin datos'),
        },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
      },
      colors: chartData.series.map((s) => s.color),
    }),
    [chartData.categories, chartData.series, groupBy]
  );

  // Show loading state
  if (loading || filtersLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-default-400'>
          {filtersLoading
            ? t('Cargando filtros...')
            : t('Cargando datos de evolución...')}
        </p>
      </div>
    );
  }

  // Show no data state
  if (!data) {
    return (
      <EmptyState title={t('No hay datos de evolución disponibles')} />
    );
  }

  return (
    <div className='w-full'>
      {/* Chart Title */}
      <div className='mb-4'>
        <div className='flex items-start justify-between gap-3'>
          <h3 className='text-lg font-semibold'>
            {t('Evolución de Precios por Color')}
          </h3>
          <SalesExcelButton
            categories={chartData.categories}
            series={chartData.series}
            currency={currency}
            fileName={`precios_evolucion_color_${selectedCaliber || 'data'}.xlsx`}
            label={t('Exportar a Excel')}
          />
        </div>
        <p className='text-default-500 text-sm'>
          {t('Moneda')}: {currency}
        </p>
        {totalKilos && (
          <p className='text-default-500 text-sm mt-1'>
            {t('Muestra')}: {totalKilos.toLocaleString('es-CL')} kg
          </p>
        )}
      </div>

      {/* Specific Filters Section */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
        <MultiSelectWithActions
          label={t('Colores a mostrar')}
          placeholder={t('Selecciona colores')}
          options={
            data
              ? Object.keys(data).map((color) => ({
                  value: color,
                  label: color,
                }))
              : []
          }
          selectedKeys={selectedColorsToShow}
          onSelectionChange={setSelectedColorsToShow}
          size='md'
        />

        <MultiSelectWithActions
          label={isDemo ? t('Variedad (Demo: Solo Lapins)') : t('Variedad')}
          placeholder={
            isDemo
              ? t('Demo: Solo Lapins disponible')
              : t('Selecciona variedades')
          }
          options={(isDemo
            ? availableFilters.varieties.filter(
                (v) => v.value.toUpperCase() === 'LAPINS'
              )
            : availableFilters.varieties
          ).map((variety) => ({
            value: variety.value,
            label: variety.value,
          }))}
          selectedKeys={selectedVarieties}
          onSelectionChange={(keys) => {
            if (!isDemo) {
              setSelectedVarieties(keys);
            }
          }}
          size='md'
          isDisabled={isDemo}
        />

        <Select
          label={t('Calibre')}
          placeholder={t('Selecciona un calibre')}
          selectedKeys={
            selectedCaliber ? new Set([selectedCaliber]) : new Set()
          }
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as string;
            if (selected) setSelectedCaliber(selected);
          }}
          size='md'
        >
          {availableFilters.calibers.map((caliber) => (
            <SelectItem
              key={caliber.value}
              value={caliber.value}
              textValue={caliber.value}
            >
              {caliber.value}
            </SelectItem>
          ))}
        </Select>

        <MultiSelectWithActions
          label={t('Formato')}
          placeholder={t('Selecciona formatos')}
          options={availableFilters.formats.map((format) => ({
            value: format.value,
            label: format.value,
          }))}
          selectedKeys={selectedFormats}
          onSelectionChange={setSelectedFormats}
          size='md'
        />
      </div>

      {/* Chart Controls */}
      <div className='flex items-center justify-end mb-6'>
        <Checkbox
          isSelected={showOwnPrices}
          onValueChange={setShowOwnPrices}
          size='md'
          color='primary'
        >
          {t('Mostrar mis precios')}
        </Checkbox>
      </div>

      <div className=''>
        {visualization === 'table' ? (
          <SalesChartTable
            categories={chartData.categories}
            series={chartData.series}
            currency={currency}
            emptyLabel={t(
              'No hay datos disponibles para los colores seleccionados'
            )}
          />
        ) : chartData.series.length > 0 ? (
          <ReactApexChart
            options={chartOptions}
            series={chartData.series}
            type='line'
            height={400}
          />
        ) : (
          <div className='flex items-center justify-center h-64'>
            <p className='text-default-400'>
              {t('No hay datos disponibles para los colores seleccionados')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorEvolutionChart;
